import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Immutable from 'immutable';

import SelectListWithKeySupport from 'components/SelectList/SelectListWithKeySupport';
import SearchUnitDestination from 'components/SearchUnitDestination/SearchUnitDestination';
import SearchUnitPopOver from 'components/SearchUnitPopOver/SearchUnitPopOver';

import * as appStateActions from 'actions/appStateActions';
import * as baseStateActions from 'actions/baseStateActions';
import * as searchUnitActions from 'actions/searchUnitActions';
import * as customControlActions from 'actions/customControlActions';

import * as optimizelyUtil from 'utils/optimizely';
import {
  debounce,
  isDomainIe,
  updateWindowLocation,
} from 'utils/utils';

import InlineSVG from 'svg-inline-react';
import globeIcon from './earth-globe.svg';

/* istanbul ignore next */
const mapStateToProps = state => ({
  appState: state.appState,
  baseState: state.baseState,
  baseStateActionsBound: state.baseState,
  dimensionsEntities: state.dimensionsEntities,
  featureFlags: state.featureFlags,
  search: state.search,
  customControlVisible: state.customControl.id === 'suggestedDestinations',
});

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    appStateActionsBound: bindActionCreators(appStateActions, dispatch),
    baseStateActionsBound: bindActionCreators(baseStateActions, dispatch),
    customControlActionsBound: bindActionCreators(customControlActions, dispatch),
    searchUnitActionsBound: bindActionCreators(searchUnitActions, dispatch),
  };
}

export class SearchUnitDestinationLayout extends Component {
  static propTypes = {
    appState: PropTypes.object.isRequired,
    appStateActionsBound: PropTypes.object.isRequired,
    baseState: PropTypes.object.isRequired,
    baseStateActionsBound: PropTypes.object.isRequired,
    customControlActionsBound: PropTypes.object,
    dimensionsEntities: PropTypes.object.isRequired,
    featureFlags: PropTypes.shape({
      SearchDestinationEverywhereEnabled: PropTypes.bool,
      get: PropTypes.function,
    }),
    isCustomControlVisible: PropTypes.bool,
    location: PropTypes.object,
    search: PropTypes.object,
    searchUnitActionsBound: PropTypes.object,
    customControlVisible: PropTypes.bool,
  };

  state = {
    isEverywherePopOverVisible: false,
  }

  componentDidUpdate() {
    const { appStateActionsBound, customControlVisible } = this.props;

    if (this.blurred && !customControlVisible) {
      appStateActionsBound.setDestinationQuery('');
      this.textVal = '';
      this.blurred = false;
    }
  }

  get anyDestination() {
    return (
      <div className="select-list__list__flexible">
        <InlineSVG className="select-list__list__flexible__icon" src={globeIcon} />
        <span className="select-list__list__element__span"><b>Flexible?</b></span>
        <span className="select-list__list__element__span"> Search 'Any Destination'</span>
      </div>
    );
  }

  get suggestedDestinations() {
    const { search, dimensionsEntities, baseState } = this.props;
    return search.toArray()
      .filter((suggestion) => {
        const isHotel = suggestion.get('type') === 'HOTEL';
        const selectedDestinations = baseState.get('D_Destination', Immutable.List());
        const isSelected = selectedDestinations.includes(suggestion.get('dimensionValueId'));

        return !isHotel && !isSelected;
      })
      .map((suggestion) => {
        const dimValId = `${suggestion.get('dimensionValueId')}`;
        const component = suggestion.get('type') === 'ANY' ? this.anyDestination : undefined;

        return {
          name: `${suggestion.get('label')}`,
          value: dimValId,
          unavailable: !dimensionsEntities.getIn([dimValId, 'available']),
          component,
        };
      });
  }

  get suggestedHotels() {
    const { search } = this.props;
    return search.toArray()
      .filter(suggestion => suggestion.get('type') === 'HOTEL')
      .map(suggestion => ({
        name: `<b>${suggestion.get('label')}</b>, in ${suggestion.get('location')}`,
        value: suggestion.get('dimensionValueId'),
        href: suggestion.get('detailURL'),
        contentType: suggestion.get('type'),
      }));
  }

  get suggestedHotelsAndDestinations() {
    return [
      ...this.suggestedDestinations,
      ...this.suggestedHotels,
    ];
  }

  onDestinationClick = (dimId) => {
    const { searchUnitActionsBound } = this.props;

    const activeIndex = this.selectListRef.state.activeIndex - 1;
    searchUnitActionsBound.addDestination(dimId)
      .then(() => {
        this.selectListRef.state.activeIndex = activeIndex;
        this.showSuggestedDestinationsControl();
        this.selectListRef.selectNext();
      });
  }

  onHotelClick = (url) => {
    updateWindowLocation(url);
  }

  getSuggestions = () => {
    const { searchUnitActionsBound } = this.props;
    const rows = this.textVal ? { rows: 10 } : { rows: 20 };
    const query = this.textVal ? this.textVal : '*';
    const searchParams = {
      ...rows,
      query,
    };
    if (isDomainIe()) {
      searchParams.filterQuery = 'locale:en-IE';
    }
    searchUnitActionsBound.suggestDestinations(searchParams)
      .then(this.showSuggestedDestinationsControl);
  }

  saveSelectListRef = (ref) => {
    this.selectListRef = ref;
  };

  showSuggestedDestinationsControl = () => {
    const { customControlActionsBound, appState } = this.props;
    const suggestedDestinations = this.suggestedHotelsAndDestinations;

    if (suggestedDestinations.length) {
      const controlContent = {
        id: 'suggestedDestinations',
        body: (
          <SelectListWithKeySupport
            ref={this.saveSelectListRef}
            controlPosition="left"
            onItemChange={this.onDestinationClick}
            emptyElementsMessage=""
            type="empty"
            elements={this.suggestedHotelsAndDestinations}
            appState={appState}
            activeItemIndex={this.selectListRef ? this.selectListRef.state.activeIndex : undefined}
            reducedPadding
            darkerBlueHotelBackground
            checkParentOnSelect
          />
        ),
      };

      customControlActionsBound.showControl({
        height: 400,
        width: 500,
        hAlign: 'left',
        boundingClientRect: this.destinationElementRef.getBoundingClientRect(),
      }, controlContent);
    } else {
      customControlActionsBound.hideControl();
    }
  }

  onKeyDown = (event) => {
    const arrowUpKeyCode = 38;
    const arrowDownKeyCode = 40;
    const enterKeyCode = 13;
    const tabKeyCode = 9;
    const escKeyCode = 27;

    if (this.selectListRef) {
      if (event.keyCode === arrowUpKeyCode) {
        this.selectListRef.selectPrevious();
        event.preventDefault();
      } else if (event.keyCode === arrowDownKeyCode) {
        this.selectListRef.selectNext();
        event.preventDefault();
      } else if (event.keyCode === tabKeyCode || event.keyCode === enterKeyCode) {
        const selectedDestination = this.suggestedHotelsAndDestinations[this.selectListRef.state.activeIndex];
        if (selectedDestination) {
          if (selectedDestination.contentType === 'HOTEL') {
            this.onHotelClick(selectedDestination.href);
          } else {
            this.onDestinationClick(selectedDestination.value, false);
          }
        }
      } else if (event.keyCode === escKeyCode) {
        this.props.appStateActionsBound.setDestinationQuery('');
        this.textVal = '';
      }
    }
  }

  getSuggestionsDebounced = debounce(this.getSuggestions, 400);

  onInputChange = (event, elementRef) => {
    const { appStateActionsBound } = this.props;

    appStateActionsBound.setDestinationQuery(event.currentTarget.value);
    this.textVal = event.currentTarget.value;

    if (this.textVal.length > 0) {
      this.hideEverywherePopOver();
    } else {
      this.getSuggestions();
    }

    this.destinationElementRef = elementRef;
    this.getSuggestionsDebounced();
  }

  onRemoveDestination = (dimId) => {
    const { baseStateActionsBound, searchUnitActionsBound } = this.props;
    baseStateActionsBound.deleteFieldKey('D_Destination', dimId);
    searchUnitActionsBound.removeDestination(dimId);
  }

  get selectedDestinations() {
    const { baseState } = this.props;
    const destinations = baseState.get('D_Destination');
    return destinations ?
      destinations
        .toArray()
        .map(id => this.props.dimensionsEntities.toJSON()[id]) :
      [];
  }

  onEverywherePopOverClick = () => {
    const { appStateActionsBound, searchUnitActionsBound } = this.props;
    const everywhereDimVal = 214;

    appStateActionsBound.setDestinationQuery('');
    searchUnitActionsBound.addDestination(everywhereDimVal);

    optimizelyUtil.fireEvent('SearchDestinationEverywherePopOver', 'clicked');

    this.hideEverywherePopOver();
  }

  onSearchUnitDestinationBlur = () => {
    this.hideEverywherePopOver();
    this.blurred = true;
  }

  showEverywherePopOver = () => {
    if (!this.selectedDestinations.length && this.isEverywhereEnabled()) {
      optimizelyUtil.fireEvent('SearchDestinationEverywherePopOver', 'displayed');
      this.setState({ isEverywherePopOverVisible: true });
    }
  }

  displayResults = (event, elementRef) => {
    this.destinationElementRef = elementRef;
    this.getSuggestions();
  }

  hideEverywherePopOver() {
    this.setState({ isEverywherePopOverVisible: false });
  }

  isEverywhereEnabled() {
    const { featureFlags } = this.props;
    return featureFlags.get('SearchDestinationEverywhereEnabled');
  }

  isEverywherePopOverVisible() {
    return this.isEverywhereEnabled() && this.state.isEverywherePopOverVisible;
  }

  render() {
    const { appState } = this.props;

    return (
      <div className="search-unit-destination-layout">
        <SearchUnitDestination
          hasNoResults={appState.get('hasNoDestinationResults')}
          onBlur={this.onSearchUnitDestinationBlur}
          onClick={this.isEverywhereEnabled() ? this.showEverywherePopOver : this.displayResults}
          onInputChange={this.onInputChange}
          onKeyDown={this.onKeyDown}
          onRemove={this.onRemoveDestination}
          queryValue={appState.get('destinationQuery')}
          selectedElements={this.selectedDestinations}
          isEverywhereEnabled={this.isEverywhereEnabled()}
        />
        { this.isEverywherePopOverVisible() && (
          <SearchUnitPopOver onClick={this.onEverywherePopOverClick}>
            <InlineSVG className="search-unit-popover__icon" src={globeIcon} />
            <h3 className="search-unit-popover__title">Flexible?</h3>
            <p className="search-unit-popover__text">Search 'Any Destination'</p>
          </SearchUnitPopOver>
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchUnitDestinationLayout);



/** WEBPACK FOOTER **
 ** ./src/layouts/SearchUnitDestinationLayout/SearchUnitDestinationLayout.jsx
 **/